import { useEffect, useState } from 'react';
import { validateDemoHubs } from '../Accounts/DemoPortalValidator';
import { List } from 'immutable';
import styled from 'styled-components';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HubPickerRow from './HubPickerRow';
import I18n from 'I18n';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { H1 } from 'foundations-components/transitional/components/H1';
import RequestStatusTypes from '../constants/RequestStatusTypes';
import links from '../utils/links';
import { getAccountsCrossHublet, setPinnedAccounts, setDefaultAccount, deleteDefaultAccount } from '../Accounts/AccountFetcher';
import { sortByField } from '../utils/sorts';
import { NavMarker } from 'react-rhumb';
import { Button } from 'foundations-components/transitional/components/Button';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const EmptyTableRow = styled.tr.withConfig({
  displayName: "HubPicker__EmptyTableRow"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return `
  &:hover {
    background-color: ${theme.color['fill-surface-default']} !important;
    cursor: default !important;
  }
  `;
});
export const EmptyTableCell = styled.td.withConfig({
  displayName: "HubPicker__EmptyTableCell"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    padding: `${theme.space['1500']} 0 !important`,
    textAlign: 'center'
  };
});
const HubPicker = ({
  redirectTo = links.homeService,
  query = {},
  filter = '',
  shouldWarnAboutObscured = false,
  hasDemoPortalGate = false
}) => {
  const [accounts, setAccounts] = useState(List());
  const [accountsRequestStatus, setAccountsRequestStatus] = useState(RequestStatusTypes.UNINITIALIZED);
  const [defaultPortal, setDefaultPortal] = useState(null);
  const [pinnedAccountIds, setPinnedAccountIds] = useState(List());
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [hubsFailedToLoad, setHubsFailedToLoad] = useState(false);
  const [validationResults, setValidationResults] = useState({});
  useEffect(() => {
    void getAccountsCrossHublet(true, false).then(value => {
      setAccounts(value.accounts);
      setDefaultPortal(value.defaultPortal);
      setAccountsRequestStatus(value.accountsRequestStatus);
      setPinnedAccountIds(value.pinnedAccounts);
      setHubsFailedToLoad(value.hubsFailedToLoad);
      if (hasDemoPortalGate && value.accounts && value.accounts.size > 0) {
        void validateDemoHubs(value.accounts).then(setValidationResults).catch(() => {});
      }
    });
  }, [hasDemoPortalGate]);
  const getFilteredAccounts = () => {
    if (!filter) {
      return accounts;
    }
    const filterText = filter.toLowerCase();
    return accounts.filter(account => account.accountName.toLowerCase().includes(filterText) || account.domain.toLowerCase().includes(filterText) || account.id.toString().includes(filterText));
  };
  const onSortChange = field => {
    return event => {
      const value = event.target.value;
      const sortedAccounts = accounts.sort(sortByField(field, value));
      setSortDir(value);
      setSortField(field);
      setAccounts(sortedAccounts);
    };
  };
  const onDefaultChange = accountId => {
    const action = defaultPortal === accountId ? () => deleteDefaultAccount() : () => setDefaultAccount(accountId);
    void action().then(resp => {
      setDefaultPortal(resp);
    }).catch(() => {
      FloatingAlertStore.addAlert({
        titleText: defaultPortal === accountId ? I18n.text('hubPicker.error.defaultPortal.deleteFailed') : I18n.text('hubPicker.error.defaultPortal.setFailed'),
        message: I18n.text('hubPicker.error.defaultPortal.message'),
        type: 'danger'
      });
    });
  };
  const onPinChange = accountId => {
    let updatedPinnedAccountIds;
    if (pinnedAccountIds.includes(accountId)) {
      const index = pinnedAccountIds.indexOf(accountId);
      updatedPinnedAccountIds = pinnedAccountIds.delete(index);
    } else {
      updatedPinnedAccountIds = pinnedAccountIds.push(accountId);
    }
    void setPinnedAccounts(updatedPinnedAccountIds).then(resp => {
      setPinnedAccountIds(resp);
    });
  };
  const renderLoader = () => {
    return /*#__PURE__*/_jsx("tr", {
      children: /*#__PURE__*/_jsx("td", {
        colSpan: 3,
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          minHeight: 50
        })
      })
    });
  };
  const renderTableContents = () => {
    const filteredAccounts = getFilteredAccounts();
    if (filteredAccounts.size === 0) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "EMPTY_TABLE_LOAD",
        children: /*#__PURE__*/_jsx(EmptyTableRow, {
          children: /*#__PURE__*/_jsx(EmptyTableCell, {
            colSpan: 3,
            children: I18n.text('hubPicker.noFilteredResults')
          })
        })
      });
    }
    const renderHubPickerRow = (account, isDefault, isPinned) => /*#__PURE__*/_jsx(HubPickerRow, {
      account: account,
      isDefault: isDefault,
      onDefaultChange: onDefaultChange,
      pinned: isPinned,
      onPinChange: onPinChange,
      query: query,
      redirectTo: redirectTo,
      hasDemoPortalGate: hasDemoPortalGate,
      validationResult: validationResults[account.id.toString()]
    }, account.id);
    const accountRows = [];
    const defaultAccount = filteredAccounts.find(account => account.id === defaultPortal);
    if (defaultAccount) {
      accountRows.push(renderHubPickerRow(defaultAccount, true, pinnedAccountIds.includes(defaultPortal)));
    }
    pinnedAccountIds.forEach(accountId => {
      if (accountId === defaultPortal) return;
      const account = filteredAccounts.find(filteredAccount => filteredAccount.id === accountId);
      if (account) {
        accountRows.push(renderHubPickerRow(account, false, true));
      }
    });
    const unpinnedAccounts = filteredAccounts.filter(account => !pinnedAccountIds.contains(account.id) && account.id !== defaultPortal);
    unpinnedAccounts.forEach(account => {
      accountRows.push(renderHubPickerRow(account, false, false));
    });
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "TABLE_LOAD",
      children: accountRows
    });
  };
  const renderObscuredWarning = () => {
    if (accounts.find(account => account.get('obscured')) === undefined) {
      return null;
    }
    return /*#__PURE__*/_jsx(Alert, {
      marginBlockEnd: "400",
      titleText: I18n.text('hubPicker.obscuredAccounts.title'),
      type: "info",
      children: I18n.text('hubPicker.obscuredAccounts.description')
    });
  };
  const renderHubsFailedToLoadWarningBanner = () => {
    return /*#__PURE__*/_jsx(Alert, {
      marginBlockEnd: "400",
      titleText: I18n.text('hubPicker.hubsFailedToLoad'),
      type: "warning"
    });
  };
  const accountsLoading = accountsRequestStatus === RequestStatusTypes.PENDING || accountsRequestStatus === RequestStatusTypes.UNINITIALIZED;
  const loading = accountsLoading;
  if (accountsRequestStatus === RequestStatusTypes.FAILED) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      type: "badRequest",
      children: /*#__PURE__*/_jsx("p", {
        children: I18n.text('error.requestFailed.description')
      })
    });
  } else if (!loading && accounts.size === 0) {
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: "1400",
      paddingBlockStart: "600",
      children: /*#__PURE__*/_jsxs(TFlex, {
        direction: "row",
        align: "center",
        justify: "center",
        gap: 20,
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx(View, {
          marginBlockEnd: "600",
          children: /*#__PURE__*/_jsxs(Box, {
            basis: 480,
            children: [/*#__PURE__*/_jsx(H1, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "hubPicker.noPortals.title"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "hubPicker.noPortals.descriptionOne"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "hubPicker.noPortals.descriptionTwo"
              })
            }), /*#__PURE__*/_jsx(Button, {
              use: "secondary",
              href: links.signuplink(),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "hubPicker.noPortals.cta"
              })
            })]
          })
        }), /*#__PURE__*/_jsx(Box, {
          basis: 260,
          children: /*#__PURE__*/_jsx(Illustration, {
            name: "team",
            width: "100%"
          })
        })]
      })
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [shouldWarnAboutObscured && renderObscuredWarning(), hubsFailedToLoad && renderHubsFailedToLoadWarningBanner(), /*#__PURE__*/_jsx(View, {
      as: "div",
      minInlineSize: "32px",
      children: /*#__PURE__*/_jsxs(Table, {
        "data-test-id": "account-picker",
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              width: '60%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '40%'
            }
          })]
        }), /*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx(SortTH, {
              sort: sortField === 'accountName' && sortDir ? sortDir : 'none',
              onSortChange: onSortChange('accountName'),
              children: I18n.text('hubPicker.name')
            }), /*#__PURE__*/_jsx(SortTH, {
              sort: sortField === 'domain' && sortDir ? sortDir : 'none',
              onSortChange: onSortChange('domain'),
              children: I18n.text('hubPicker.domain')
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: loading ? renderLoader() : renderTableContents()
        })]
      })
    })]
  });
};
export default HubPicker;